// [PURPOSE]: Map a spell's duration string to minutes. Used by the spell-effect
//   expiry service to decide when a non-concentration spell's mechanical
//   effects should be retracted from the registry.
// [WHY]: Spell durations are authored as English strings ("1 minute",
//   "8 hours", "Until dispelled"). The expiry service needs a numeric value to
//   compare against rest events. Pulling the parser into its own module keeps
//   it unit-testable and shared across consumers (CW Spell Creator validation,
//   SpellsPanel "duration left" badge, expiry service itself).
// [INTEGRATION]: spellCastingService.castSpell + spellEffectExpiry.js consume
//   parseDurationToMinutes to attach a numeric durationMinutes on registry
//   metadata.

// Common SRD duration strings, lowercased for matching. Values are minutes.
//   null = "no auto-expire" (Until dispelled / Special / Concentration-only)
//   0    = "instantaneous" — no ongoing duration
const DURATION_TABLE = Object.freeze({
  'instantaneous': 0,
  '1 round': 0.1,         // ~6 seconds — sub-minute, treat as ephemeral
  '1 minute': 1,
  '10 minutes': 10,
  '1 hour': 60,
  '8 hours': 480,
  '12 hours': 720,
  '24 hours': 1440,
  '7 days': 60 * 24 * 7,
  '10 days': 60 * 24 * 10,
  '30 days': 60 * 24 * 30,
  'until dispelled': null,
  'special': null,
});

const SPECIAL_SENTINELS = Object.freeze(['until dispelled', 'special']);

/**
 * Parse a spell duration string into minutes.
 *
 * Returns:
 *   - A non-negative number if the duration is finite (e.g., 60 for "1 hour").
 *   - null if the duration is open-ended ("Until dispelled", "Special") OR if
 *     the string starts with "Concentration" (callers should use the
 *     concentration system for cleanup, not the duration parser).
 *   - 0 for "Instantaneous" (no ongoing duration — most damage spells).
 *   - null for unrecognized strings (defensive — don't auto-expire effects
 *     we can't reason about).
 *
 * @param {string|undefined} duration
 * @returns {number|null}
 */
export function parseDurationToMinutes(duration) {
  if (duration === undefined || duration === null) return null;
  const lc = String(duration).trim().toLowerCase();
  if (!lc) return null;

  // Concentration spells are cleaned up by concentrationSystem, NOT the
  // duration-based expiry service. Returning null tells the caller to skip.
  if (lc.startsWith('concentration')) return null;

  if (lc in DURATION_TABLE) return DURATION_TABLE[lc];
  if (SPECIAL_SENTINELS.includes(lc)) return null;

  // "1 minute / 1 round / etc." — try a generic regex match for "<n> <unit>".
  // This handles edge cases like "2 hours" or "5 minutes" that aren't in the
  // canonical table.
  const m = lc.match(/^(\d+)\s+(round|rounds|minute|minutes|hour|hours|day|days|week|weeks)$/);
  if (m) {
    const n = parseInt(m[1], 10);
    const unit = m[2];
    if (!Number.isFinite(n) || n < 0) return null;
    if (unit === 'round' || unit === 'rounds') return n * 0.1;
    if (unit === 'minute' || unit === 'minutes') return n;
    if (unit === 'hour' || unit === 'hours') return n * 60;
    if (unit === 'day' || unit === 'days') return n * 24 * 60;
    if (unit === 'week' || unit === 'weeks') return n * 7 * 24 * 60;
  }

  return null;
}

/**
 * Convenience predicate: should a duration auto-expire on a given rest type?
 * Short rest = 1 hour → expires durations ≤ 60 minutes.
 * Long  rest = 8 hours → expires durations ≤ 480 minutes.
 * Day pass    = 24 hours → expires durations ≤ 1440 minutes.
 */
export function expiresOnShortRest(durationMinutes) {
  return typeof durationMinutes === 'number' && durationMinutes > 0 && durationMinutes <= 60;
}
export function expiresOnLongRest(durationMinutes) {
  return typeof durationMinutes === 'number' && durationMinutes > 0 && durationMinutes <= 480;
}
export function expiresOnDayPass(durationMinutes) {
  return typeof durationMinutes === 'number' && durationMinutes > 0 && durationMinutes <= 1440;
}
